import {
  getContentItemsV1,
  getContentByRouteV1,
  getContentByIdV1,
  getLanguagesV1,
  getTranslationsByCultureV1,
} from '@/services/platform/api/cms/cms';
import {
  GetContentItemsV1Params,
  IApiContentResponseModel,
  PagedIApiContentResponseModel,
  LanguageInfo,
  TranslationResponse,
  AppSettingsContentResponseModel,
  MemberTypeContentResponseModel,
  MemberTypeContentResponseModelContentType,
} from '@/services/platform/api/model';
import { getConfig } from '../../../../../config/environment';

/**
 * Fetches a CMS page by its URL path, typed as `T`.
 *
 * The `handle` is normalised before dispatch — leading and trailing slashes are stripped
 * to match the Umbraco Delivery API path format (e.g. `"uk/book"`, not `"/uk/book/"`).
 *
 * In draft mode the `Preview: true` header is sent so Umbraco returns unpublished content.
 * If route resolution fails in draft mode, the function attempts a second lookup by preview
 * cookie ID (currently disabled — see inline comment).
 *
 * On a network error outside draft mode, a hardcoded stub is returned for the root path
 * (`""` / `"/"`) and the error path (`"error"`) so the app can render in degraded state.
 *
 * @param handle - URL path to resolve (e.g. `"/uk/book"` or `"uk/book"`).
 * @param culture - BCP 47 locale tag forwarded as `Accept-Language`.
 * @param isDraftMode - When `true`, requests preview/draft content from the CMS.
 * @returns The page typed as `T` on HTTP 200, or `undefined` when not found / on error.
 */
function shopSectionStub<T>(normalizedPath: string): T | undefined {
  const parts = normalizedPath.split('/');
  const shopIdx = parts.indexOf('shop');
  const candidate = shopIdx !== -1 && shopIdx < parts.length - 1 ? parts[shopIdx + 1] : undefined;
  if (!candidate) return undefined;
  return { contentType: 'shopSection', name: `Shop ${candidate}` } as T;
}

export async function getPage<T>(
  handle: string,
  culture?: string,
  isDraftMode?: boolean
): Promise<T | undefined> {
  try {
    const config = getConfig(); // Ensure config is loaded and environment variables are accessed early
    const instanceId = config?.cmsInstanceId || 'default';

    // Normalize the path: remove leading/trailing slashes and ensure proper format
    // Umbraco paths should be like "uk" or "uk/products/product-name" (no leading slash)
    let normalizedPath = handle.trim();
    if (normalizedPath.startsWith('/')) {
      normalizedPath = normalizedPath.slice(1);
    }
    if (normalizedPath.endsWith('/')) {
      normalizedPath = normalizedPath.slice(0, -1);
    }

    const headers: Record<string, string> = {};

    if (culture) {
      headers['Accept-Language'] = culture;
    }

    // When in preview mode, request preview content from Umbraco
    // The Preview header tells Umbraco Delivery API to return draft/unpublished content
    if (isDraftMode) {
      headers['Preview'] = 'true';
    }

    let response = await getContentByRouteV1(instanceId, normalizedPath, {
      headers,
      // In development, always bypass cache. In production, use ISR for published content, no cache for draft content
      ...{ cache: 'no-store' },
    });

    if (response.status === 404) {
      const pathParts = normalizedPath.split('/');
      const shopIdx = pathParts.indexOf('shop');
      if (shopIdx !== -1 && shopIdx < pathParts.length - 1 && !pathParts[shopIdx + 1].startsWith('gender_')) {
        pathParts[shopIdx + 1] = `gender_${pathParts[shopIdx + 1]}`;
        const fallbackPath = pathParts.join('/');
        const fallbackResponse = await getContentByRouteV1(instanceId, fallbackPath, {
          headers,
          ...{ cache: 'no-store' },
        });
        if (fallbackResponse.status === 200) {
          response = fallbackResponse;
          normalizedPath = fallbackPath;
        }
      }
    }

    // Log detailed error information only for server errors (5xx), not for expected 404s
    if (response.status >= 500) {
      console.error(`[getPage] Server error fetching page by path: ${handle}`, {
        normalizedPath,
        status: response.status,
        culture,
        isDraftMode,
        headers: Object.fromEntries(Object.entries(headers)),
      });
    }

    if (response.status === 200) {
      const page: T = response.data as T;
      if (!page) {
        console.warn(`[getPage] API returned 200 but no data for path: ${normalizedPath}`);
        return undefined;
      }
      return page;
    }

    if (isDraftMode) {
      const previewContentId = undefined; //(await cookies()).get('umbraco-preview-id')?.value;

      if (previewContentId) {
        const previewResponse = await getContentByIdV1(instanceId, previewContentId, {
          headers,
          cache: 'no-store',
        });

        if (previewResponse.status === 200) {
          const page: T = previewResponse.data as T;
          return page;
        }

        console.warn(
          `getPage: Preview content not found by ID ${previewContentId} (status: ${previewResponse.status})`
        );
      } else {
        console.warn('getPage: Preview cookie not found, unable to fetch draft content by ID.');
      }
    }

    return shopSectionStub<T>(normalizedPath) ?? undefined;
  } catch (error) {
    console.warn(`Error fetching page ${handle}:`, error);

    // Provide fallback data for common pages (only when not in draft mode)
    if (!isDraftMode) {
      const sectionStub = shopSectionStub<T>(handle);
      if (sectionStub) return sectionStub;

      if (handle === '' || handle === '/') {
        return {
          name: 'Home',
          route: { path: '/' },
          properties: {
            pageTitle: 'Welcome to Demo Client Mobile',
            pageDescription: 'A modern multilingual multi-regional site built with Umbraco and Next.js',
          },
        } as T;
      }

      if (handle === 'error' || handle === '/error') {
        return {
          name: 'Error',
          route: { path: '/error' },
          properties: {
            pageTitle: 'Page Not Found',
            pageDescription: 'The page you are looking for could not be found.',
          },
        } as T;
      }
    }

    return undefined;
  }
}

/**
 * Queries the CMS content delivery API for a paged list of items.
 *
 * `params` maps directly to the Umbraco Delivery API query string and supports
 * `fetch` (e.g. `"children:<id>"`, `"descendants:<id>"`), `filter`, `expand`,
 * `skip`, and `take`.
 *
 * @param params - Query parameters forwarded to `getContentItemsV1`.
 * @param culture - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `PagedIApiContentResponseModel` (`items` + `total`) on HTTP 200.
 * @returns `undefined` on non-2xx response or network error.
 */
export async function getContents(
  params: GetContentItemsV1Params,
  culture?: string
): Promise<PagedIApiContentResponseModel | undefined> {
  try {
    const config = getConfig(); // Ensure config is loaded and environment variables are accessed early
    const response = await getContentItemsV1(config?.cmsInstanceId || 'default', params, {
      headers: culture
        ? {
            'Accept-Language': culture,
          }
        : {},
    });

    if (response.status === 200) {
      const data: PagedIApiContentResponseModel = response.data as PagedIApiContentResponseModel;
      return data;
    } else {
      console.warn(`Error fetching navigation (status: ${response.status})`);
      return undefined;
    }
  } catch (error) {
    console.warn('Error fetching navigation:', error);
    return undefined;
  }
}

/**
 * Fetches a single CMS content item by its node ID.
 *
 * @param id - The CMS node GUID of the content item to fetch.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as `Accept-Language`
 *   so the API returns content in the correct language.
 * @returns `IApiContentResponseModel` on HTTP 200.
 * @returns `undefined` on 404 (item not found) or network error.
 */
export async function getContentById(
  id: string,
  locale?: string
): Promise<IApiContentResponseModel | undefined> {
  try {
    const config = getConfig();
    const response = await getContentByIdV1(config?.cmsInstanceId || 'default', id, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as IApiContentResponseModel;
    }

    console.warn(`getContentById: unexpected status ${response.status} for id "${id}"`);
    return undefined;
  } catch (error) {
    console.warn(`getContentById: request failed for id "${id}":`, error);
    return undefined;
  }
}

/**
 * Fetches all languages configured for the CMS instance.
 *
 * @returns Array of `LanguageInfo` on HTTP 200.
 * @returns `undefined` on non-2xx response or network error.
 */
export async function getLanguages(): Promise<LanguageInfo[] | undefined> {
  try {
    const config = getConfig(); // Ensure config is loaded and environment variables are accessed early
    const response = await getLanguagesV1(config?.cmsInstanceId || 'default', {
      headers: {},
    });

    if (response.status === 200) {
      const data: LanguageInfo[] = response.data as LanguageInfo[];
      return data;
    } else {
      console.warn(`Error fetching languages (status: ${response.status})`);
      return undefined;
    }
  } catch (error) {
    console.warn('Error fetching languages:', error);
    return undefined;
  }
}

/**
 * Fetches the translation dictionary for a given culture.
 *
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`).
 * @returns `TranslationResponse` containing key/value translation pairs on HTTP 200.
 * @returns `undefined` on non-2xx response or network error.
 */
export async function getTranslations(culture: string): Promise<TranslationResponse | undefined> {
  try {
    const config = getConfig(); // Ensure config is loaded and environment variables are accessed early
    const response = await getTranslationsByCultureV1(config?.cmsInstanceId || 'default', culture, {
      headers: {},
    });

    if (response.status === 200) {
      const data: TranslationResponse = response.data as TranslationResponse;
      return data;
    } else {
      console.warn(`Error fetching translations (status: ${response.status})`);
      return undefined;
    }
  } catch (error) {
    console.warn('Error fetching translations:', error);
    return undefined;
  }
}

/**
 * Fetches the app settings page for the given culture.
 *
 * Resolves the well-known path `/{locale}/settings` via `getPage`, where `locale` is
 * the primary language tag of `culture` (e.g. `"en"` from `"en-GB"`).
 *
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`).
 * @returns `AppSettingsContentResponseModel` when the settings page is found.
 * @returns `undefined` when not found or on network error.
 */
export async function getSettings(culture: string): Promise<AppSettingsContentResponseModel | undefined> {
  try {
    const settings = await getPage<AppSettingsContentResponseModel>(
      `/${culture?.toLowerCase()?.split('-')[0]}/settings`,
      culture,
      false
    );

    if (settings) {
      return settings;
    } else {
      console.warn(`Error fetching settings (culture: ${culture})`);
      return undefined;
    }
  } catch (error) {
    console.warn('Error fetching settings:', error);
    return undefined;
  }
}

/**
 * Fetches a single member type content item by matching its `type` property
 * against the provided string.
 *
 * Member type nodes live under the `memberTypeRepository` content node. This
 * function queries the repository's direct children and returns the first item
 * whose `properties.type` matches `type` (case-insensitive).
 *
 * @param repositoryId - CMS node ID of the member-types repository
 *   (`settings.properties.memberTypeRepository.id`).
 * @param type - The membership type string to match (e.g. `"Annual Member"`).
 * @param locale - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns The matching `MemberTypeContentResponseModel` on success.
 * @returns `undefined` when not found or on error.
 */
export async function getMemberTypeByType(
  repositoryId: string,
  type: string,
  locale: string
): Promise<MemberTypeContentResponseModel | undefined> {
  try {
    const result = await getContents(
      {
        fetch: `children:${repositoryId}`,
        filter: [`contentType:${MemberTypeContentResponseModelContentType.memberType}`],
        skip: 0,
        take: 50,
      },
      locale
    );

    const normalised = type.toLowerCase();
    const match = result?.items?.find(item => {
      const mt = item as MemberTypeContentResponseModel;
      return (mt.properties?.type ?? mt.name ?? '').toLowerCase() === normalised;
    });

    return match as MemberTypeContentResponseModel | undefined;
  } catch (error) {
    console.warn(`getMemberTypeByType: request failed for type "${type}":`, error);
    return undefined;
  }
}
